import React, { useState } from 'react';
import { FormatDate } from 'components/dates/FormatDate';
import NewFolderModal from './Modals/NewFolderModal';
import SearchBar from 'components/search/SearchBar';
import { NButton, NIcon, NTable } from '@innersource-nn/novoui-react';
import { SearchFilter } from 'types/Filters';
import useFolderList from '../hooks/useFolderList';
import { StorageLocation } from 'types/Dataset';
import clsx from 'clsx';
import Pagination from 'components/pager/Pagination';

interface FolderListProps {
    isUserAdmin: boolean;
}

const tableHeaders = [
    { header: 'Name', key: 'label' },
    { header: 'S3 Prefix', key: 'S3Prefix' },
    { header: 'Created', key: 'created' },
    { header: 'Path', key: 'path' },
];

const FolderList = ({ isUserAdmin }: FolderListProps) => {
    const [modalOpen, setModalOpen] = useState(false);

    const [filter, setFilter] = useState<SearchFilter>({
        page: 1,
        pageSize: 10,
        term: '',
    });

    const {
        items,
        folders,
        error,
        loading,
        showSearchbar,
        targetS3BucketName,
        fetchData,
    } = useFolderList(filter);

    const adjustItems = (folders: StorageLocation[]) => {
        return folders.map((item) => {
            return {
                ...item,
                created: FormatDate(item.created),
                path: `s3://${targetS3BucketName}/${item.S3Prefix}`,
            };
        });
    };

    const showTable = !error && (loading || showSearchbar);

    return (
        <div>
            <NewFolderModal
                visible={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    fetchData();
                }}
            />

            {showTable ? (
                <div>
                    <div className="flex gap-16 mb-8 justify-between items-center">
                        <div className="w-full">
                            <SearchBar
                                term={filter.term ?? ''}
                                onTermChange={(e) => {
                                    setFilter({ ...filter, term: e });
                                }}
                            />
                        </div>

                        <NButton
                            leftIcon="book"
                            square
                            disabled={!isUserAdmin}
                            onClick={() => setModalOpen(true)}
                            variant="outline"
                            size="small"
                        >
                            New folder
                        </NButton>
                    </div>

                    <NTable
                        loadXMoreRowsAtATime={loading ? 5 : undefined}
                        headers={tableHeaders}
                        items={loading ? [{}] : adjustItems(folders)}
                        columnBorder={true}
                        rowKey="locationUri"
                    />
                    {!loading && items.count > 0 && (
                        <Pagination
                            items={items}
                            filter={filter}
                            setFilter={setFilter}
                        />
                    )}
                </div>
            ) : (
                <div className="empty-place-holder mt-12">
                    <div className="flex flex-col items-center">
                        <div
                            className={clsx(
                                isUserAdmin
                                    ? 'bg-light-blue-200'
                                    : 'bg-golden-sun-200',
                                'round-div-icon-middle  h-[88px] w-[88px] ',
                            )}
                        >
                            <NIcon
                                iconName="book"
                                size={48}
                                className="fill-true-blue"
                            />
                        </div>
                        <div className="flex flex-col items-center">
                            <p className="text-true-blue text-center mt-8">
                                {isUserAdmin
                                    ? `You don’t have any folders in your dataset yet. You can start by creating one here!`
                                    : `There doesn’t seem to be any items available for this dataset yet. Check back again later!`}
                            </p>
                            {isUserAdmin && (
                                <NButton
                                    className="mt-16"
                                    leftIcon="circleAdd"
                                    square
                                    onClick={() => setModalOpen(true)}
                                    variant="fill"
                                >
                                    New folder
                                </NButton>
                            )}{' '}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FolderList;
